import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPinOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { api, isApiError } from '@/lib/api';
import local from '@/pages/finance/finance.module.css';
import {
  buildPatch,
  fieldErrorMap,
  toForm,
  type BranchForm,
  type BranchInfo,
} from './branch-details';
import styles from './EditBranchDialog.module.css';

/**
 * "Edit clinic information" (Admin v2 Branches & rooms, RL-548 + RL-1120):
 * address/contact plus the app details from API RL-1080 (WhatsApp, map pin,
 * parking, panels accepted, services, MOH number). Saves only changed fields
 * with `PATCH /branches/:id`; validation errors show next to the field.
 */
export function EditBranchDialog({
  branch: b,
  onClose,
}: {
  branch: BranchInfo;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<BranchForm>(() => toForm(b));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<BranchInfo>(`/branches/${b.id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['branches'] }),
  });

  const set = (key: keyof BranchForm, value: string) => {
    setF((s) => ({ ...s, [key]: value }));
    setErrors(({ [key]: _, ...rest }) => rest);
  };

  async function submit() {
    setFailure(null);
    const { body, errors: invalid } = buildPatch(b, f);
    setErrors(invalid);
    if (Object.keys(invalid).length) return;
    if (!Object.keys(body).length) {
      toast.show({ tone: 'info', title: 'Nothing changed' });
      onClose();
      return;
    }
    try {
      await save.mutateAsync(body);
      toast.show({ tone: 'success', title: 'Clinic information saved' });
      onClose();
    } catch (err) {
      if (!isApiError(err)) {
        setFailure({ message: 'Could not save the clinic information.' });
        return;
      }
      if (err.code === 'BRANCH_LOCATION_INCOMPLETE')
        setErrors({ latitude: err.problem?.title ?? err.message });
      else setErrors(fieldErrorMap(err.fieldErrors));
      setFailure({ message: err.message, requestId: err.requestId });
    }
  }

  const field = (
    key: keyof BranchForm,
    label: string,
    hint?: ReactNode,
    props: Record<string, unknown> = {},
  ) => (
    <Field key={key} label={label} hint={hint} error={errors[key]}>
      <Input
        value={f[key]}
        onChange={(e) => set(key, e.target.value)}
        {...props}
      />
    </Field>
  );
  const area = (key: keyof BranchForm, label: string, hint: ReactNode) => (
    <Field key={key} label={label} hint={hint} error={errors[key]}>
      <textarea
        className={local.textarea}
        value={f[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    </Field>
  );
  const pinSet = Boolean(f.latitude.trim() || f.longitude.trim());

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Edit clinic information · ${b.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => void submit()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={local.form}>
        {failure && (
          <p className={styles.errorNote} role="alert">
            {failure.message}
            {failure.requestId && (
              <span>{t('common.requestId', { id: failure.requestId })}</span>
            )}
          </p>
        )}
        <h3 className={styles.section}>Address and contact</h3>
        {field('addressLine1', 'Address line 1')}
        {field('addressLine2', 'Address line 2 (optional)')}
        <div className={local.twoCol}>
          {field('postcode', 'Postcode')}
          {field('city', 'City')}
        </div>
        {field('state', 'State')}
        {field('phone', 'Phone')}
        {field(
          'whatsapp',
          'WhatsApp (optional)',
          'International format, e.g. +60 12-345 6789. Leave blank to hide it in the app.',
          { inputMode: 'tel' },
        )}
        {field('email', 'Email (optional)')}

        <h3 className={styles.section}>Map pin for “Get directions”</h3>
        <div className={local.twoCol}>
          {field('latitude', 'Latitude', 'e.g. 2.2 (between -90 and 90)', {
            inputMode: 'decimal',
          })}
          {field(
            'longitude',
            'Longitude',
            'e.g. 102.25 (between -180 and 180)',
            { inputMode: 'decimal' },
          )}
        </div>
        {pinSet && (
          <div className={styles.pinActions}>
            <Button
              size="sm"
              variant="ghost"
              icon={<MapPinOff size={16} />}
              onClick={() => {
                set('latitude', '');
                set('longitude', '');
              }}
            >
              Remove map pin
            </Button>
          </div>
        )}
        {field(
          'parkingNote',
          'Parking (optional)',
          'Shown in the app, e.g. Free parking in front. Up to 300 characters.',
          { maxLength: 300 },
        )}

        <h3 className={styles.section}>Shown in the app</h3>
        {area(
          'panelsAccepted',
          'Panels accepted',
          'One per line, by the names patients know. Leave empty to show nothing.',
        )}
        {area(
          'servicesOffered',
          'Services offered',
          'One per line, in the order the app lists them. Leave empty to show facilities instead.',
        )}
        {field(
          'facilities',
          'Facilities',
          'Separate with commas, e.g. Sample draw, Dispensary',
        )}
        {field(
          'mohRegistrationNo',
          'MOH registration (optional)',
          'Shown to patients in the app when set.',
          { maxLength: 40 },
        )}
      </div>
    </Dialog>
  );
}
